import React from 'react';
import { Download, Printer, Calendar } from 'lucide-react';
import PrintableReport from './PrintableReport';
import { Link } from 'react-router-dom';

const AccentButton = ({ variant = 'default', className = '', ...props }) => {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors';

  const variants = {
    default:
      'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50',
    blue:
      'bg-[#2563EB] text-white border border-[#2563EB] hover:bg-[#1D4ED8] shadow-sm',
    outline:
      'bg-white text-gray-600 border border-[#E5E7EB] hover:bg-gray-50',
  };

  return (
    <button
      className={`${base} ${variants[variant] || variants.default} ${className}`}
      type="button"
      {...props}
    />
  );
};

const ReportLayout = ({
  title,
  description,
  dateRange,
  onDateRangeChange,
  onExportCsv,
  onExportExcel,
  onPrint,
  printLink,
  kpis = [],
  leftSlot,
  rightSlot,
  tablesSlot,
  bg = 'bg-white',
}) => {
  const showDateRange = !!dateRange && typeof onDateRangeChange === 'function';

  const handlePrint = () => {
    if (typeof onPrint === 'function') return onPrint();
    window.print();
  };

  const handleExport = (type) => {
    if (type === 'pdf') return handlePrint();
    if (type === 'csv') {
      if (typeof onExportCsv === 'function') return onExportCsv();
    }
    if (type === 'excel') {
      if (typeof onExportExcel === 'function') return onExportExcel();
    }
  };

  // Build the report content
  const reportContent = (
    <>
      {/* KPI cards */}
      {kpis.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 report-section">
          {kpis.map((k) => (
            <div
              key={k.label}
              className="rounded-xl bg-white border border-[#EEF2F7] p-5 shadow-[0_2px_6px_rgba(0,0,0,0.05)] metric-card"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${k.iconBg || 'bg-gray-100'} flex items-center justify-center ${k.iconColor || 'text-gray-600'}`}>
                  {k.icon}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-[#6B7280]">{k.label}</p>
                <p className="text-[26px] font-bold text-[#1F2937] mt-1 leading-tight">{k.value}</p>
                {k.foot ? (
                  <p className="text-xs font-semibold text-[#16A34A] mt-1">
                    {k.foot}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Two-column analytical section */}
      {(leftSlot || rightSlot) ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6 report-section">
          <div className="lg:col-span-3 space-y-6">{leftSlot}</div>
          <div className="lg:col-span-2 space-y-6">{rightSlot}</div>
        </div>
      ) : null}

      {/* Detailed tables beneath charts */}
      {tablesSlot ? <div className="space-y-6 report-section">{tablesSlot}</div> : null}
    </>
  );

  return (
    <PrintableReport
      title={title}
      dateRange={dateRange}
      onExportCsv={onExportCsv}
      onPrint={onPrint}
    >
      {/* Page Header - Screen only, not in print header */}
      <div className="no-print flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[28px] font-bold text-[#1F2937]">{title}</h1>
          {description ? (
            <p className="text-sm text-[#6B7280] mt-1">{description}</p>
          ) : null}
          {showDateRange ? (
            <DateRangeControls
              dateRange={dateRange}
              onDateRangeChange={onDateRangeChange}
            />
          ) : null}
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end shrink-0">
          <AccentButton variant="blue" onClick={() => handleExport('csv')}>
            <Download size={16} />
            Export Report
          </AccentButton>

          {printLink ? (
            <Link
              to={printLink}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            >
              <Printer size={16} />
              Print Report
            </Link>
          ) : (
            <AccentButton variant="default" onClick={handlePrint}>
              <Printer size={16} />
              Print
            </AccentButton>
          )}
        </div>
      </div>

      {reportContent}
    </PrintableReport>
  );
};

const pad2 = (n) => String(n).padStart(2, '0');

const toISODate = (d) => {
  const year = d.getFullYear();
  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${year}-${month}-${day}`;
};

const monthKeyFromDateRange = (dateRange) => {
  const start = dateRange?.start ? new Date(dateRange.start) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  const y = start.getFullYear();
  const m = start.getMonth();
  return { year: y, monthIndex: m };
};

const monthLabel = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const DateRangeControls = ({ dateRange, onDateRangeChange }) => {
  const derived = monthKeyFromDateRange(dateRange);
  const selectedMonthIndex = derived?.monthIndex ?? null;
  const selectedYear = derived?.year ?? null;

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  const effectiveYear = selectedYear ?? currentYear;
  const effectiveMonthIndex = selectedMonthIndex ?? new Date().getMonth();

  const formatDateRange = () => {
    if (!dateRange?.start || !dateRange?.end) return 'Select date range';
    const s = new Date(dateRange.start);
    const e = new Date(dateRange.end);
    const opts = { day: '2-digit', month: 'short', year: 'numeric' };
    return `${s.toLocaleDateString('en-KE', opts)} - ${e.toLocaleDateString('en-KE', opts)}`;
  };

  const setMonth = (year, monthIndex) => {
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);
    onDateRangeChange({
      ...dateRange,
      start: toISODate(startDate),
      end: toISODate(endDate),
    });
  };

  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <select
          className="pl-3 pr-8 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200 bg-white h-10"
          value={effectiveMonthIndex}
          onChange={(e) => setMonth(effectiveYear, Number(e.target.value))}
        >
          {monthLabel.map((lbl, idx) => (
            <option key={lbl} value={idx}>{lbl}</option>
          ))}
        </select>

        <select
          className="pl-3 pr-8 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:ring-2 focus:ring-blue-200 bg-white h-10"
          value={effectiveYear}
          onChange={(e) => setMonth(Number(e.target.value), effectiveMonthIndex)}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <span className="text-gray-300 font-semibold">|</span>

      <div className="flex items-center gap-2 border border-[#E5E7EB] rounded-lg bg-white h-10 px-3">
        <Calendar size={16} className="text-gray-400" />
        <span className="text-sm text-gray-700">{formatDateRange()}</span>
      </div>
    </div>
  );
};

export default ReportLayout;